/**
 * Replay Viewer screen.
 * Loads replays from IndexedDB or file, and plays them back with a
 * canvas-based stage, verbose turn log, and playback controls.
 *
 * Playback behaviour:
 *  - Shows perfect information (both teams, all HP, species, items, abilities).
 *  - Pauses before each turn so the viewer can inspect the board.
 *  - Verbose log shows turn counters, actions, damage, and faints.
 *  - Spacebar toggles play/pause.
 *  - Speed select: 0.25x - 4x. 1x = 1 turn per second.
 */

import type { ScreenId } from "./home";
import type { Replay, BattleState, PokemonBattleState, PlayerAction } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, toast } from "../components";
import { dbGetAll } from "../../data/db";
import { DB_STORES } from "../../config/constants";
import { downloadJson, pickFile, readFileAsText } from "../../data/backup";
import { pokemonFrontUrl, pokemonBackUrl, backgroundImageUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";

const STAGE_WIDTH = 640;
const STAGE_HEIGHT = 400;
const SPRITE_SIZE = 80;
const BENCH_SIZE = 40;
const HP_BAR_WIDTH = 120;
const HP_BAR_HEIGHT = 8;

const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4];
const DEFAULT_SPEED = 1; // turns per second

export function renderReplay(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: "Replays" }));
  header.appendChild(button("Back to Home", () => navigate("home"), { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main replay-main" });

  // Actions bar
  const actionsBar = el("div", { className: "actions-bar" });
  actionsBar.appendChild(button("Load from File", async () => {
    const file = await pickFile("application/json");
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const replay = JSON.parse(text) as Replay;
      showReplay(replay);
    } catch {
      toast("Failed to load replay", "error");
    }
  }, { variant: "secondary" }));
  main.appendChild(actionsBar);

  // Stored replays list
  const listSection = el("section", { className: "replay-list-section" });
  listSection.appendChild(el("h2", { text: "Saved Replays" }));
  const replayList = el("div", { className: "replay-list" });
  listSection.appendChild(replayList);
  main.appendChild(listSection);

  // Viewer area
  const viewerArea = el("div", { className: "replay-viewer-area" });
  main.appendChild(viewerArea);

  container.appendChild(main);

  let currentReplay: Replay | null = null;
  let currentTurnIndex = -1; // -1 = initial state, 0..n-1 = after turn N
  let isPlaying = false;
  let speed = DEFAULT_SPEED;
  let playbackTimer: ReturnType<typeof setTimeout> | null = null;
  let keyHandlerAttached = false;

  // Load saved replays
  void loadSavedReplays(replayList);

  function loadSavedReplays(list: HTMLElement): Promise<void> {
    clearChildren(list);
    return dbGetAll<Replay>(DB_STORES.REPLAYS).then((replays) => {
      if (replays.length === 0) {
        list.appendChild(el("p", { text: "No saved replays yet.", className: "replay-empty" }));
        return;
      }
      for (const r of replays) {
        const item = el("div", { className: "replay-item" });
        const info = el("div", { className: "replay-item-info" });
        info.appendChild(el("span", { text: `${r.p1Profile.name} vs ${r.p2Profile.name}`, className: "replay-item-title" }));
        info.appendChild(el("span", { text: new Date(r.createdAt).toLocaleString(), className: "replay-item-date" }));
        item.appendChild(info);
        item.appendChild(button("Watch", () => showReplay(r), { variant: "secondary" }));
        item.appendChild(button("Download JSON", () => downloadJson(r, `replay-${r.createdAt}.json`), { variant: "secondary" }));
        item.appendChild(button("Share", () => shareReplay(r), { variant: "secondary" }));
        list.appendChild(item);
      }
    });
  }

  async function shareReplay(replay: Replay): Promise<void> {
    const text = JSON.stringify(replay, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast("Replay JSON copied to clipboard", "success");
      } else {
        const blob = new Blob([text], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        toast(`Replay share URL ready: ${url}`, "info");
      }
    } catch {
      toast("Failed to share replay", "warning");
    }
  }

  function showReplay(replay: Replay): void {
    stopPlayback();
    currentReplay = replay;
    currentTurnIndex = -1;
    isPlaying = false;
    speed = DEFAULT_SPEED;
    renderViewer();
  }

  function renderViewer(): void {
    clearChildren(viewerArea);
    if (!currentReplay) return;

    viewerArea.appendChild(el("h2", { text: "Replay Viewer" }));
    viewerArea.appendChild(el("p", { text: `${currentReplay.p1Profile.name} vs ${currentReplay.p2Profile.name}`, className: "replay-matchup" }));

    // Canvas stage
    const stage = el("div", { className: "replay-stage" });
    const canvas = el("canvas", { attrs: { width: String(STAGE_WIDTH), height: String(STAGE_HEIGHT) } }) as HTMLCanvasElement;
    stage.appendChild(canvas);
    viewerArea.appendChild(stage);

    // Log
    const log = el("div", { className: "replay-log" });
    viewerArea.appendChild(log);

    // Controls
    const controls = el("div", { className: "replay-controls" });
    const playBtn = button("Play", togglePlay, { variant: "primary" });
    const prevBtn = button("◀ Prev", () => { stopPlayback(); goToTurn(currentTurnIndex - 1); }, { variant: "secondary" });
    const nextBtn = button("Next ▶", () => { stopPlayback(); goToTurn(currentTurnIndex + 1); }, { variant: "secondary" });
    const speedSelect = el("select", { className: "replay-speed-select", attrs: { "aria-label": "Playback speed" } }) as HTMLSelectElement;
    for (const s of SPEED_OPTIONS) {
      const opt = el("option", { text: `${s}x`, attrs: { value: String(s) } });
      speedSelect.appendChild(opt);
    }
    speedSelect.value = String(speed);
    speedSelect.addEventListener("change", () => {
      speed = Number(speedSelect.value);
    });
    const turnLabel = el("span", { text: turnLabelText() });
    controls.appendChild(playBtn);
    controls.appendChild(prevBtn);
    controls.appendChild(nextBtn);
    controls.appendChild(speedSelect);
    controls.appendChild(turnLabel);
    viewerArea.appendChild(controls);

    const pausedHint = el("div", { className: "replay-paused-hint", text: "Paused — press Space or Next to advance" });
    viewerArea.appendChild(pausedHint);

    // Spacebar toggle
    if (!keyHandlerAttached) {
      keyHandlerAttached = true;
      document.addEventListener("keydown", handleKeydown);
    }

    // Initial render
    drawStage(canvas);
    rebuildLog(log);
    updateControls({ playBtn, nextBtn, prevBtn, turnLabel, pausedHint });

    function handleKeydown(e: KeyboardEvent): void {
      if (!currentReplay) return;
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        e.preventDefault();
        togglePlay();
      }
    }
  }

  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable;
  }

  function turnLabelText(): string {
    if (!currentReplay) return "";
    const total = currentReplay.turns.length;
    if (currentTurnIndex < 0) return `Start / ${total} turns`;
    if (currentTurnIndex >= total) return `End / ${total} turns`;
    return `Turn ${currentTurnIndex + 1} / ${total}`;
  }

  function updateControls(refs: { playBtn: HTMLElement; nextBtn: HTMLElement; prevBtn: HTMLElement; turnLabel: HTMLElement; pausedHint: HTMLElement }): void {
    refs.playBtn.textContent = isPlaying ? "Pause" : "Play";
    refs.turnLabel.textContent = turnLabelText();
    const total = currentReplay?.turns.length ?? 0;
    refs.prevBtn.toggleAttribute("disabled", currentTurnIndex <= -1);
    refs.nextBtn.toggleAttribute("disabled", currentTurnIndex >= total);
    refs.pausedHint.textContent = isPlaying ? "Playing..." : currentTurnIndex >= total ? "Replay finished" : "Paused — press Space or Next to advance";
  }

  function togglePlay(): void {
    if (!currentReplay) return;
    if (currentTurnIndex >= currentReplay.turns.length) {
      // Restart from beginning when reaching the end
      currentTurnIndex = -1;
    }
    isPlaying = !isPlaying;
    if (isPlaying) {
      scheduleNextTurn();
    } else {
      stopPlayback();
    }
    refreshViewer();
  }

  function scheduleNextTurn(): void {
    if (!currentReplay || !isPlaying) return;
    const total = currentReplay.turns.length;
    if (currentTurnIndex >= total) {
      isPlaying = false;
      refreshViewer();
      return;
    }
    const delayMs = 1000 / speed;
    playbackTimer = setTimeout(() => {
      goToTurn(currentTurnIndex + 1);
      if (isPlaying) scheduleNextTurn();
    }, delayMs);
  }

  function stopPlayback(): void {
    isPlaying = false;
    if (playbackTimer) {
      clearTimeout(playbackTimer);
      playbackTimer = null;
    }
  }

  function goToTurn(index: number): void {
    if (!currentReplay) return;
    const total = currentReplay.turns.length;
    currentTurnIndex = Math.max(-1, Math.min(total, index));
    refreshViewer();
  }

  function refreshViewer(): void {
    // Re-render the viewer completely to keep the implementation simple.
    // In a larger app we would keep element references and update them in place.
    renderViewer();
  }

  function rebuildLog(logEl: HTMLElement): void {
    clearChildren(logEl);
    if (!currentReplay) return;
    const states = getStateSequence(currentReplay);
    for (let i = 0; i < currentReplay.turns.length; i++) {
      const turn = currentReplay.turns[i];
      const prev = states[i];
      const next = states[i + 1];
      logEl.appendChild(el("div", { className: "replay-log-entry turn", text: `Turn ${turn.turn}` }));
      for (const pa of turn.actions) {
        logEl.appendChild(el("div", { className: "replay-log-entry", text: describeAction(pa, currentReplay) }));
      }
      // Damage and faints
      for (const side of ["p1", "p2"] as const) {
        const teamPrev = prev[side];
        const teamNext = next[side];
        for (let j = 0; j < teamPrev.length; j++) {
          const before = teamPrev[j];
          const after = teamNext[j];
          if (!before || !after) continue;
          if (after.hp < before.hp) {
            const dmg = before.hp - after.hp;
            logEl.appendChild(el("div", {
              className: "replay-log-entry damage",
              text: `${after.nickname || after.species} took ${dmg} damage!`,
            }));
          }
          if (before.hp > 0 && after.hp <= 0) {
            logEl.appendChild(el("div", {
              className: "replay-log-entry faint",
              text: `${after.nickname || after.species} fainted!`,
            }));
          }
          if (before.status !== after.status && after.status) {
            logEl.appendChild(el("div", {
              className: "replay-log-entry",
              text: `${after.nickname || after.species} is now ${after.status}.`,
            }));
          }
        }
      }
      if (next.ended && next.winner) {
        const winnerName = next.winner === "p1" ? currentReplay.p1Profile.name : currentReplay.p2Profile.name;
        logEl.appendChild(el("div", { className: "replay-log-entry faint", text: `Winner: ${winnerName}` }));
      }
    }
    logEl.scrollTop = logEl.scrollHeight;
  }

  function getStateSequence(replay: Replay): BattleState[] {
    const states: BattleState[] = [replay.initialState];
    for (const t of replay.turns) states.push(t.stateAfter);
    return states;
  }

  function describeAction(pa: PlayerAction, replay: Replay): string {
    const trainerName = pa.player === "p1" ? replay.p1Profile.name : replay.p2Profile.name;
    const action = pa.action;
    if (action.type === "move") {
      return `${trainerName} used ${action.move}.`;
    } else if (action.type === "switch") {
      return `${trainerName} switched to slot ${action.slot + 1}.`;
    } else if (action.type === "struggle") {
      return `${trainerName} used Struggle.`;
    }
    return `${trainerName} took an action.`;
  }

  async function drawStage(canvas: HTMLCanvasElement): Promise<void> {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, STAGE_WIDTH, STAGE_HEIGHT);

    const replay = currentReplay;
    if (!replay) return;
    const state = getCurrentState(replay);
    if (!state) return;

    // Background
    const bgUrl = await resolveSpriteUrl(backgroundImageUrl(replay.config.battleBackground));
    if (bgUrl) {
      const img = await loadImage(bgUrl);
      ctx.drawImage(img, 0, 0, STAGE_WIDTH, STAGE_HEIGHT);
    }

    // Trainer names
    ctx.fillStyle = "#e8e8f0";
    ctx.font = 'bold 18px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = "left";
    ctx.fillText(replay.p1Profile.name, 16, 32);
    ctx.textAlign = "right";
    ctx.fillText(replay.p2Profile.name, STAGE_WIDTH - 16, 32);

    // Turn / weather
    ctx.textAlign = "center";
    ctx.font = '14px "Segoe UI", system-ui, sans-serif';
    const turnText = currentTurnIndex < 0 ? "Start" : `Turn ${currentTurnIndex + 1}`;
    const weatherText = state.weather ? `Weather: ${state.weather}` : "";
    ctx.fillText(`${turnText}${weatherText ? ` • ${weatherText}` : ""}`, STAGE_WIDTH / 2, 24);

    // Draw P1 team (bottom)
    drawTeam(ctx, state.p1, state.activeP1, 16, STAGE_HEIGHT - 16, true);
    // Draw P2 team (top)
    drawTeam(ctx, state.p2, state.activeP2, STAGE_WIDTH - 16, 48, false);

    // Winner overlay
    if (state.ended && state.winner) {
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(0, 0, STAGE_WIDTH, STAGE_HEIGHT);
      ctx.fillStyle = state.winner === "p1" ? "#5cb85c" : "#d9534f";
      ctx.font = 'bold 36px "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const winnerName = state.winner === "p1" ? replay.p1Profile.name : replay.p2Profile.name;
      ctx.fillText(`${winnerName} Wins!`, STAGE_WIDTH / 2, STAGE_HEIGHT / 2);
      ctx.textBaseline = "alphabetic";
    }
  }

  function drawTeam(
    ctx: CanvasRenderingContext2D,
    team: PokemonBattleState[],
    activeIndex: number,
    baseX: number,
    baseY: number,
    isBack: boolean,
  ): void {
    const activeMon = team[activeIndex];
    if (activeMon) {
      drawPokemon(ctx, activeMon, baseX - (isBack ? 0 : SPRITE_SIZE), baseY - SPRITE_SIZE, SPRITE_SIZE, isBack);
    }
    // Bench icons row
    const benchStartX = isBack ? 16 : STAGE_WIDTH - 16 - team.length * (BENCH_SIZE + 4);
    const benchY = isBack ? STAGE_HEIGHT - BENCH_SIZE - 8 : 8;
    for (let i = 0; i < team.length; i++) {
      const mon = team[i];
      const x = benchStartX + i * (BENCH_SIZE + 4);
      drawBenchIcon(ctx, mon, x, benchY, BENCH_SIZE, i === activeIndex);
    }
  }

  function drawPokemon(
    ctx: CanvasRenderingContext2D,
    mon: PokemonBattleState,
    x: number,
    y: number,
    size: number,
    isBack: boolean,
  ): void {
    void resolveSpriteUrl(isBack ? pokemonBackUrl(mon.species, mon.shiny) : pokemonFrontUrl(mon.species, mon.shiny)).then(async (url) => {
      if (!url) return;
      const img = await loadImage(url);
      ctx.drawImage(img, x, y, size, size);
    });
    // Info text
    ctx.textAlign = isBack ? "left" : "right";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#e8e8f0";
    ctx.font = 'bold 14px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(mon.nickname || mon.species, isBack ? x : x + size, y - 24);
    ctx.font = '12px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(`Lv${mon.level}${mon.status ? ` (${mon.status})` : ""}`, isBack ? x : x + size, y - 8);
    // HP bar
    drawHpBar(ctx, isBack ? x : x + size - HP_BAR_WIDTH, y - 6, mon.hp, mon.maxHp);
  }

  function drawBenchIcon(
    ctx: CanvasRenderingContext2D,
    mon: PokemonBattleState,
    x: number,
    y: number,
    size: number,
    active: boolean,
  ): void {
    void resolveSpriteUrl(pokemonFrontUrl(mon.species, mon.shiny)).then(async (url) => {
      if (!url) return;
      const img = await loadImage(url);
      ctx.globalAlpha = mon.hp <= 0 ? 0.4 : active ? 1 : 0.7;
      ctx.drawImage(img, x, y, size, size);
      ctx.globalAlpha = 1;
    });
    // HP bar
    drawHpBar(ctx, x, y + size + 2, mon.hp, mon.maxHp, size);
    if (active) {
      ctx.strokeStyle = "#5a8fd8";
      ctx.lineWidth = 2;
      ctx.strokeRect(x - 2, y - 2, size + 4, size + 4);
    }
  }

  function drawHpBar(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    hp: number,
    maxHp: number,
    width = HP_BAR_WIDTH,
  ): void {
    const pct = maxHp > 0 ? Math.max(0, hp / maxHp) : 0;
    const color = pct > 0.5 ? "#5cb85c" : pct > 0.25 ? "#f0ad4e" : "#d9534f";
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(x, y, width, HP_BAR_HEIGHT);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, width * pct, HP_BAR_HEIGHT);
    ctx.fillStyle = "#e8e8f0";
    ctx.font = '10px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(`${hp}/${maxHp}`, x, y + HP_BAR_HEIGHT + 2);
  }

  function getCurrentState(replay: Replay): BattleState | null {
    if (currentTurnIndex < 0) return replay.initialState;
    if (currentTurnIndex >= replay.turns.length) {
      return replay.turns[replay.turns.length - 1]?.stateAfter ?? null;
    }
    return replay.turns[currentTurnIndex].stateAfter;
  }

  function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = src;
    });
  }
}
